import { Structure, StructureElement, StructureProperties, Unit } from '../../mol-model/structure';
import { Queries } from '../../mol-model/structure/query';
import { QueryContext } from '../../mol-model/structure/query/context';
import { StructureSelection } from '../../mol-model/structure/query/selection';
import { StructureSelectionQueries } from '../../mol-plugin-state/helpers/structure-selection-query';
import { PluginContext } from '../../mol-plugin/context';
import type { StructureSelectionQuery } from '../../mol-plugin-state/helpers/structure-selection-query';
import { AtomRef, Numbering, ResidueRange, Selector } from './types';
import type { ToolSchema, ToolSchemaObject } from './registration';

const MAX_SELECTOR_DEPTH = 8;
const MAX_SELECTOR_NODES = 64;
const MAX_ARRAY_ITEMS = 100;
const MAX_STRING_LENGTH = 128;
const MAX_RADIUS = 15;
const MIN_RADIUS = 0;

const runtimeQueryLabels = {
    isTransmembrane: 'Residues Embedded in Membrane',
    isBuried: 'Buried Protein Residues',
    isAccessible: 'Accessible Protein Residues',
    hasClash: 'Residues with Clashes',
} as const;

const selectorKinds = ['current-selection', 'current-focus', 'hovered', 'structure', 'chain', 'residues', 'atoms', 'named', 'around', 'union', 'intersect', 'subtract'] as const;

export const selectorLimits = { maxDepth: MAX_SELECTOR_DEPTH, maxNodes: MAX_SELECTOR_NODES, maxArrayItems: MAX_ARRAY_ITEMS, maxStringLength: MAX_STRING_LENGTH, minRadiusExclusive: MIN_RADIUS, maxRadius: MAX_RADIUS };
export function selectorQueryNames() { return [...Object.keys(StructureSelectionQueries), ...Object.keys(runtimeQueryLabels)]; }

/** Compact discovery schema; parseSelector remains the strict recursive trust-boundary validator. */
export const selectorPropertySchema: ToolSchemaObject = {
    type: 'object',
    description: 'Mol* selector object. Call get-selector-guide for fields and examples.',
    properties: { kind: { enum: selectorKinds } },
    required: ['kind'],
    additionalProperties: true,
};

export function selectorPropertiesRequestSchema(selectorProperties: readonly string[], properties: Readonly<Record<string, ToolSchema>> = {}, required: readonly string[] = selectorProperties): ToolSchemaObject {
    const requestProperties: Record<string, ToolSchema> = { ...properties };
    for (const property of selectorProperties) requestProperties[property] = selectorPropertySchema;
    return { type: 'object', properties: requestProperties, required, additionalProperties: false };
}

export function selectorRequestSchema(property = 'selector'): ToolSchemaObject {
    return selectorPropertiesRequestSchema([property]);
}

export interface LoadedStructure {
    readonly ref: string
    readonly structure: Structure
}

export interface StructureQueryRegistry {
    readonly list: readonly StructureSelectionQuery[]
}

export interface SelectorResolverContext {
    readonly structures: readonly LoadedStructure[]
    readonly currentSelection?: StructureElement.Loci
    readonly currentFocus?: StructureElement.Loci
    readonly hovered?: StructureElement.Loci
    readonly structureQueryRegistry?: StructureQueryRegistry
}

export function createSelectorResolverContext(plugin: PluginContext): SelectorResolverContext {
    const structures = plugin.managers.structure.hierarchy.current.structures.flatMap(entry => entry.cell.obj?.data ? [{ ref: entry.cell.transform.ref, structure: entry.cell.obj.data }] : []);
    const parentLoci = (loci: StructureElement.Loci | undefined) => {
        if (!loci) return void 0;
        const parent = plugin.helpers.substructureParent.get(loci.structure)?.obj?.data;
        return parent ? StructureElement.Loci.remap(loci, parent) : loci;
    };
    let currentSelection: StructureElement.Loci | undefined;
    for (const entry of structures) {
        const loci = plugin.managers.structure.selection.getLoci(entry.structure);
        if (StructureElement.Loci.is(loci) && !StructureElement.Loci.isEmpty(loci)) { currentSelection = loci; break; }
    }
    const hovered = plugin.behaviors.interaction.hover.value.current.loci;
    return { structures, currentSelection: parentLoci(currentSelection), currentFocus: parentLoci(plugin.managers.structure.focus.current?.loci), hovered: StructureElement.Loci.is(hovered) ? parentLoci(hovered) : void 0, structureQueryRegistry: plugin.query.structure.registry };
}

export interface ResolvedSelector {
    readonly ref: string
    readonly structure: Structure
    readonly loci: StructureElement.Loci
}

export interface SelectorResolution {
    readonly ok: true
    readonly entries: readonly ResolvedSelector[]
}

export interface SelectorResolutionError {
    readonly ok: false
    readonly error: string
    readonly hint: string
}

export type SelectorResolutionResult = SelectorResolution | SelectorResolutionError;

interface SelectorInput {
    readonly kind?: unknown
    readonly ref?: unknown
    readonly chain?: unknown
    readonly numbering?: unknown
    readonly ranges?: unknown
    readonly atoms?: unknown
    readonly query?: unknown
    readonly of?: unknown
    readonly radius?: unknown
    readonly wholeResidues?: unknown
    readonly from?: unknown
    readonly to?: unknown
    readonly insCode?: unknown
    readonly seq?: unknown
    readonly atom?: unknown
}

interface SelectorParseState {
    nodes: number
}

/** Parse untrusted WebMCP selector data before it reaches Mol* query APIs. */
export function parseSelector(value: unknown): Selector | undefined {
    return parseSelectorAt(value, 0, { nodes: 0 });
}

export function resolveSelector(value: unknown, context: SelectorResolverContext): SelectorResolutionResult {
    const selector = parseSelector(value);
    if (selector === void 0) return fail('Invalid selector.', 'Call get-selector-guide for supported forms, query names, and bounds.');
    if (context.structures.length === 0) return fail('No structures are loaded.', 'Load a structure, then retry the selector.');

    try {
        const entries = context.structures.map(entry => ({ ref: entry.ref, structure: entry.structure, loci: resolveForStructure(selector, entry, context) }));
        const targetError = getTargetError(selector, context, entries);
        if (targetError !== void 0) return targetError;
        if (entries.every(entry => StructureElement.Loci.isEmpty(entry.loci))) {
            return fail('The selector matched no atoms.', 'Check the structure reference, chain, and author versus label numbering.');
        }
        return { ok: true, entries };
    } catch {
        return fail('The selector could not be resolved.', 'Check that the target exists and retry with author or label numbering.');
    }
}

function parseSelectorAt(value: unknown, depth: number, state: SelectorParseState): Selector | undefined {
    if (depth > MAX_SELECTOR_DEPTH || ++state.nodes > MAX_SELECTOR_NODES || !isSelectorInput(value)) return void 0;
    const input = value;
    if (!isString(input.kind)) return void 0;

    switch (input.kind) {
        case 'current-selection':
        case 'current-focus':
        case 'hovered': return onlyKeys(input, ['kind']) ? { kind: input.kind } : void 0;
        case 'structure': return isBoundedString(input.ref) && onlyKeys(input, ['kind', 'ref']) ? { kind: 'structure', ref: input.ref } : void 0;
        case 'chain': {
            return isOptionalRef(input.ref) && isBoundedString(input.chain) && isOptionalNumbering(input.numbering) && onlyKeys(input, ['kind', 'ref', 'chain', 'numbering'])
                ? makeChain(input.ref, input.chain, input.numbering)
                : void 0;
        }
        case 'residues': {
            const ranges = parseRanges(input.ranges);
            return ranges !== void 0 && onlyKeys(input, ['kind', 'ranges']) ? { kind: 'residues', ranges } : void 0;
        }
        case 'atoms': {
            const atoms = parseAtoms(input.atoms);
            return atoms !== void 0 && onlyKeys(input, ['kind', 'atoms']) ? { kind: 'atoms', atoms } : void 0;
        }
        case 'named': return isBoundedString(input.query) && onlyKeys(input, ['kind', 'query']) ? { kind: 'named', query: input.query } : void 0;
        case 'around': {
            const of = parseSelectorAt(input.of, depth + 1, state);
            return of !== void 0 && isRadius(input.radius) && isOptionalBoolean(input.wholeResidues) && onlyKeys(input, ['kind', 'of', 'radius', 'wholeResidues'])
                ? optionalWholeResidues({ kind: 'around', of, radius: input.radius }, input.wholeResidues)
                : void 0;
        }
        case 'union':
        case 'intersect':
        case 'subtract': {
            const of = parseSelectors(input.of, depth + 1, state);
            return of !== void 0 && onlyKeys(input, ['kind', 'of']) ? { kind: input.kind, of } : void 0;
        }
        default: return void 0;
    }
}

function resolveForStructure(selector: Selector, entry: LoadedStructure, context: SelectorResolverContext): StructureElement.Loci {
    switch (selector.kind) {
        case 'current-selection': return remapExternal(context.currentSelection, entry.structure);
        case 'current-focus': return remapExternal(context.currentFocus, entry.structure);
        case 'hovered': return remapExternal(context.hovered, entry.structure);
        case 'structure': return selector.ref === entry.ref ? StructureElement.Loci.all(entry.structure) : StructureElement.Loci.none(entry.structure);
        case 'chain': return matchesRef(selector.ref, entry.ref, context.structures.length) ? queryLoci(entry.structure, chainQuery(selector.chain, selector.numbering ?? 'auth')) : StructureElement.Loci.none(entry.structure);
        case 'residues': return resolveResidues(selector.ranges, entry, context.structures.length);
        case 'atoms': return resolveAtoms(selector.atoms, entry, context.structures.length);
        case 'named': return resolveNamed(selector.query, entry.structure, context.currentSelection, context.structureQueryRegistry);
        case 'around': return around(resolveForStructure(selector.of, entry, context), entry.structure, selector.radius, selector.wholeResidues === true);
        case 'union': return selector.of.reduce((loci, child) => StructureElement.Loci.union(loci, resolveForStructure(child, entry, context)), StructureElement.Loci.none(entry.structure));
        case 'intersect': return selector.of.reduce((loci, child) => StructureElement.Loci.intersect(loci, resolveForStructure(child, entry, context)), StructureElement.Loci.all(entry.structure));
        case 'subtract': return selector.of.slice(1).reduce((loci, child) => StructureElement.Loci.subtract(loci, resolveForStructure(child, entry, context)), resolveForStructure(selector.of[0], entry, context));
    }
}

function chainQuery(chain: string, numbering: Numbering) {
    return Queries.generators.chains({
        unitTest: ctx => Unit.isAtomic(ctx.element.unit),
        chainTest: ctx => (numbering === 'auth' ? StructureProperties.chain.auth_asym_id : StructureProperties.chain.label_asym_id)(ctx.element) === chain,
    });
}

function residueQuery(range: ResidueRange) {
    const numbering = range.numbering ?? 'auth';
    const sequence = numbering === 'auth' ? StructureProperties.residue.auth_seq_id : StructureProperties.residue.label_seq_id;
    const chain = numbering === 'auth' ? StructureProperties.chain.auth_asym_id : StructureProperties.chain.label_asym_id;
    const to = range.to ?? range.from;
    return Queries.generators.residues({
        unitTest: ctx => Unit.isAtomic(ctx.element.unit),
        chainTest: ctx => chain(ctx.element) === range.chain,
        residueTest: ctx => sequence(ctx.element) >= range.from && sequence(ctx.element) <= to && (range.insCode === void 0 || StructureProperties.residue.pdbx_PDB_ins_code(ctx.element) === range.insCode),
    });
}

function atomQuery(atom: AtomRef) {
    const numbering = atom.numbering ?? 'auth';
    const sequence = numbering === 'auth' ? StructureProperties.residue.auth_seq_id : StructureProperties.residue.label_seq_id;
    const chain = numbering === 'auth' ? StructureProperties.chain.auth_asym_id : StructureProperties.chain.label_asym_id;
    return Queries.generators.atoms({
        unitTest: ctx => Unit.isAtomic(ctx.element.unit),
        chainTest: ctx => chain(ctx.element) === atom.chain,
        residueTest: ctx => sequence(ctx.element) === atom.seq && (atom.insCode === void 0 || StructureProperties.residue.pdbx_PDB_ins_code(ctx.element) === atom.insCode),
        atomTest: ctx => StructureProperties.atom.label_atom_id(ctx.element) === atom.atom,
    });
}

function resolveResidues(ranges: readonly ResidueRange[], entry: LoadedStructure, structureCount: number) {
    return ranges.reduce((loci, range) => !matchesRef(range.ref, entry.ref, structureCount) ? loci : StructureElement.Loci.union(loci, queryLoci(entry.structure, residueQuery(range))), StructureElement.Loci.none(entry.structure));
}

function resolveAtoms(atoms: readonly AtomRef[], entry: LoadedStructure, structureCount: number) {
    return atoms.reduce((loci, atom) => !matchesRef(atom.ref, entry.ref, structureCount) ? loci : StructureElement.Loci.union(loci, queryLoci(entry.structure, atomQuery(atom))), StructureElement.Loci.none(entry.structure));
}

interface NamedQueryMatch { readonly query: StructureSelectionQuery }
type NamedQueryResolution = NamedQueryMatch | undefined;

function resolveNamed(query: string, structure: Structure, currentSelection: StructureElement.Loci | undefined, registry: StructureQueryRegistry | undefined) {
    const named = resolveNamedQuery(query, registry);
    if (named === void 0) return StructureElement.Loci.none(structure);
    const remappedSelection = currentSelection === void 0 ? void 0 : StructureElement.Loci.remap(currentSelection, structure);
    const selection = remappedSelection === void 0
        ? void 0
        : StructureSelection.Singletons(structure, StructureElement.Loci.toStructure(remappedSelection));
    return StructureElement.Loci.fromQuery(structure, named.query.query, new QueryContext(structure, { currentSelection: selection }));
}

function resolveNamedQuery(name: string, registry: StructureQueryRegistry | undefined): NamedQueryResolution {
    const builtIn = Object.entries(StructureSelectionQueries).find(([key]) => key === name)?.[1];
    if (builtIn !== void 0) return { query: builtIn };
    const label = runtimeQueryLabel(name);
    if (label === void 0) return void 0;
    const matches = (registry?.list ?? []).filter(query => query.label === label);
    return matches.length === 1 ? { query: matches[0] } : void 0;
}

function around(loci: StructureElement.Loci, structure: Structure, radius: number, wholeResidues: boolean) {
    if (StructureElement.Loci.isEmpty(loci)) return loci;
    const query = Queries.modifiers.includeSurroundings(ctx => StructureSelection.Singletons(structure, StructureElement.Loci.toStructure(loci)), { radius, wholeResidues });
    return StructureElement.Loci.fromQuery(structure, query);
}

function queryLoci(structure: Structure, query: ReturnType<typeof Queries.generators.atoms>) {
    return StructureElement.Loci.fromQuery(structure, query);
}

function remapExternal(loci: StructureElement.Loci | undefined, structure: Structure) {
    return loci === void 0 ? StructureElement.Loci.none(structure) : StructureElement.Loci.remap(loci, structure);
}

function getTargetError(selector: Selector, context: SelectorResolverContext, entries: readonly ResolvedSelector[]): SelectorResolutionError | undefined {
    const refs = selectorRefs(selector);
    for (const ref of refs) if (!context.structures.some(entry => entry.ref === ref)) return fail(`Structure '${ref}' is not loaded.`, 'Call list-structures and use one of its refs.');
    for (const name of namedQueryNames(selector)) {
        const named = resolveNamedQuery(name, context.structureQueryRegistry);
        if (named === void 0) return fail(`Named query '${name}' is not available.`, 'Use a built-in Mol* query name such as all, polymer, ligand, or water, or one documented extension alias.');
    }
    if (requiresImplicitRef(selector) && context.structures.length !== 1) return fail('A structure ref is required when multiple structures are loaded.', 'Set ref explicitly, or target the only loaded structure.');
    if (selector.kind === 'chain' && !entries.some(entry => !StructureElement.Loci.isEmpty(entry.loci))) {
        const otherNumbering = selector.numbering === 'label' ? 'auth' : 'label';
        if (entries.some(entry => !StructureElement.Loci.isEmpty(queryLoci(entry.structure, chainQuery(selector.chain, otherNumbering))))) return fail(`Chain '${selector.chain}' uses ${otherNumbering} numbering.`, `Retry with numbering: '${otherNumbering}'.`);
        return fail(`Chain '${selector.chain}' was not found.`, 'Check chain identifiers with get-selection or list-structures.');
    }
    return void 0;
}

function runtimeQueryLabel(name: string): string | undefined {
    switch (name) {
        case 'isTransmembrane': return runtimeQueryLabels.isTransmembrane;
        case 'isBuried': return runtimeQueryLabels.isBuried;
        case 'isAccessible': return runtimeQueryLabels.isAccessible;
        case 'hasClash': return runtimeQueryLabels.hasClash;
        default: return void 0;
    }
}

function namedQueryNames(selector: Selector): readonly string[] {
    switch (selector.kind) {
        case 'named': return [selector.query];
        case 'around': return namedQueryNames(selector.of);
        case 'union': case 'intersect': case 'subtract': return selector.of.flatMap(namedQueryNames);
        default: return [];
    }
}

function selectorRefs(selector: Selector): readonly string[] {
    switch (selector.kind) {
        case 'structure': return [selector.ref];
        case 'chain': return selector.ref === void 0 ? [] : [selector.ref];
        case 'residues': return selector.ranges.flatMap(range => range.ref === void 0 ? [] : [range.ref]);
        case 'atoms': return selector.atoms.flatMap(atom => atom.ref === void 0 ? [] : [atom.ref]);
        case 'around': return selectorRefs(selector.of);
        case 'union': case 'intersect': case 'subtract': return selector.of.flatMap(selectorRefs);
        default: return [];
    }
}

function requiresImplicitRef(selector: Selector): boolean {
    switch (selector.kind) {
        case 'chain': return selector.ref === void 0;
        case 'residues': return selector.ranges.some(range => range.ref === void 0);
        case 'atoms': return selector.atoms.some(atom => atom.ref === void 0);
        case 'around': return requiresImplicitRef(selector.of);
        case 'union': case 'intersect': case 'subtract': return selector.of.some(requiresImplicitRef);
        default: return false;
    }
}

function matchesRef(ref: string | undefined, current: string, structureCount: number) {
    return ref === current || (ref === void 0 && structureCount === 1);
}

function parseRanges(value: unknown): ResidueRange[] | undefined {
    if (!Array.isArray(value) || value.length === 0 || value.length > MAX_ARRAY_ITEMS) return void 0;
    const ranges: ResidueRange[] = [];
    for (const item of value) {
        if (!isSelectorInput(item) || !isOptionalRef(item.ref) || !isBoundedString(item.chain) || !isInteger(item.from) || !isOptionalInteger(item.to) || !isOptionalString(item.insCode) || !isOptionalNumbering(item.numbering) || !onlyKeys(item, ['ref', 'chain', 'from', 'to', 'insCode', 'numbering'])) return void 0;
        if (item.to !== void 0 && item.to < item.from) return void 0;
        const range: ResidueRange = { chain: item.chain, from: item.from };
        if (item.ref !== void 0) range.ref = item.ref;
        if (item.to !== void 0) range.to = item.to;
        if (item.insCode !== void 0) range.insCode = item.insCode;
        if (item.numbering !== void 0) range.numbering = item.numbering;
        ranges.push(range);
    }
    return ranges;
}

function parseAtoms(value: unknown): AtomRef[] | undefined {
    if (!Array.isArray(value) || value.length === 0 || value.length > MAX_ARRAY_ITEMS) return void 0;
    const atoms: AtomRef[] = [];
    for (const item of value) {
        if (!isSelectorInput(item) || !isOptionalRef(item.ref) || !isBoundedString(item.chain) || !isInteger(item.seq) || !isOptionalString(item.insCode) || !isBoundedString(item.atom) || !isOptionalNumbering(item.numbering) || !onlyKeys(item, ['ref', 'chain', 'seq', 'insCode', 'atom', 'numbering'])) return void 0;
        const atom: AtomRef = { chain: item.chain, seq: item.seq, atom: item.atom };
        if (item.ref !== void 0) atom.ref = item.ref;
        if (item.insCode !== void 0) atom.insCode = item.insCode;
        if (item.numbering !== void 0) atom.numbering = item.numbering;
        atoms.push(atom);
    }
    return atoms;
}

function parseSelectors(value: unknown, depth: number, state: SelectorParseState): Selector[] | undefined {
    if (!Array.isArray(value) || value.length === 0 || value.length > MAX_ARRAY_ITEMS) return void 0;
    const selectors: Selector[] = [];
    for (const item of value) {
        const selector = parseSelectorAt(item, depth, state);
        if (selector === void 0) return void 0;
        selectors.push(selector);
    }
    return selectors;
}

function isSelectorInput(value: unknown): value is SelectorInput {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function onlyKeys(value: SelectorInput, keys: readonly string[]) {
    return Object.keys(value).every(key => keys.includes(key));
}
function isString(value: unknown): value is string { return typeof value === 'string'; }
function isBoundedString(value: unknown): value is string { return isString(value) && value.length > 0 && value.length <= MAX_STRING_LENGTH; }
function isOptionalString(value: unknown): value is string | undefined { return value === void 0 || isBoundedString(value); }
function isInteger(value: unknown): value is number { return typeof value === 'number' && Number.isSafeInteger(value); }
function isOptionalInteger(value: unknown): value is number | undefined { return value === void 0 || isInteger(value); }
function isOptionalBoolean(value: unknown): value is boolean | undefined { return value === void 0 || typeof value === 'boolean'; }
function isOptionalRef(value: unknown): value is string | undefined { return value === void 0 || isBoundedString(value); }
function isRadius(value: unknown): value is number { return typeof value === 'number' && Number.isFinite(value) && value > MIN_RADIUS && value <= MAX_RADIUS; }
function isOptionalNumbering(value: unknown): value is Numbering | undefined { return value === void 0 || value === 'auth' || value === 'label'; }
function makeChain(ref: string | undefined, chain: string, numbering: Numbering | undefined): Selector {
    if (ref === void 0 && numbering === void 0) return { kind: 'chain', chain };
    if (ref === void 0) return { kind: 'chain', chain, numbering };
    if (numbering === void 0) return { kind: 'chain', ref, chain };
    return { kind: 'chain', ref, chain, numbering };
}
function optionalWholeResidues(value: { kind: 'around', of: Selector, radius: number, wholeResidues?: boolean }, wholeResidues: boolean | undefined): Selector { return wholeResidues === void 0 ? value : { ...value, wholeResidues }; }
function fail(error: string, hint: string): SelectorResolutionError { return { ok: false, error, hint }; }
